<script setup lang="ts">
import { Check, ChevronsUpDown, Search, X } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Command, CommandGroup, CommandItem, CommandList } from '@/components/ui/command'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import type { FieldValue } from '@/types/reports'

const props = defineProps<{
  modelValue: string[]
  options: FieldValue[]
}>()

const emit = defineEmits<{ 'update:modelValue': [string[]] }>()

const open = ref(false)
const query = ref('')

watch(open, (isOpen) => {
  if (isOpen) query.value = ''
})

const selected = computed(() =>
  props.modelValue.map((value) => props.options.find((o) => o.value === value) ?? { value, label: value }),
)

const visibleOptions = computed(() => {
  const q = query.value.trim().toLowerCase()
  return q ? props.options.filter((o) => o.label.toLowerCase().includes(q)) : props.options
})

function toggle(value: string) {
  const next = props.modelValue.includes(value)
    ? props.modelValue.filter((v) => v !== value)
    : [...props.modelValue, value]
  emit('update:modelValue', next)
}
</script>

<template>
  <div class="flex flex-wrap items-center gap-1.5">
    <Badge v-for="option in selected" :key="option.value" variant="secondary" class="gap-1 font-normal">
      {{ option.label }}
      <button type="button" :aria-label="`Remove ${option.label}`" @click="toggle(option.value)">
        <X class="size-3" />
      </button>
    </Badge>

    <Popover v-model:open="open">
      <PopoverTrigger as-child>
        <Button variant="outline" role="combobox" :aria-expanded="open" class="h-8 justify-between font-normal">
          <span class="text-muted-foreground">{{ selected.length ? 'Add value…' : 'Select value…' }}</span>
          <ChevronsUpDown class="ml-2 size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent class="w-60 p-0" align="start">
        <div class="border-border focus-within:border-ring flex items-center gap-2 border-b px-3 transition-colors">
          <Search class="text-muted-foreground size-4 shrink-0" />
          <Input
            v-model="query"
            placeholder="Search values…"
            class="h-10 rounded-none border-0 px-0 shadow-none focus-visible:ring-0"
          />
        </div>
        <Command>
          <CommandList>
            <p v-if="visibleOptions.length === 0" class="text-muted-foreground py-6 text-center text-sm">
              No values found.
            </p>
            <CommandGroup v-else>
              <CommandItem
                v-for="option in visibleOptions"
                :key="option.value"
                :value="option.value"
                @select="toggle(option.value)"
              >
                <Check :class="cn('size-4', modelValue.includes(option.value) ? 'opacity-100' : 'opacity-0')" />
                {{ option.label }}
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  </div>
</template>
